import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepButton,
  Stepper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import LaunchIcon from '@mui/icons-material/Launch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useFeatureFlags } from '../../features/FeatureFlagsContext';
import config from '../../config';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';
import { EntityStatGrid, PublicationCard } from '../../components/workspace/EntityPanels';
import { StatusChip, formatDate, labelize } from '../../components/workspace/WorkspaceStatus';
import {
  canonicalRequest,
  MediaGalleryPicker,
  RemoteEntityGalleryPicker,
  SectionCard,
  ValidationSummary,
} from './CanonicalEditorCommon';
import { CreatePublicationDialog, ManagePublicationDialog } from './CanonicalPublicationDialogs';
import ProductPlacementDesigner from './ProductPlacementDesigner';
import { firstOfferPreviewImage } from './productPreview';
import {
  buildLaunchListingPayload,
  buildLaunchProductPayload,
  defaultProductName,
  emptyLaunchDraft,
  launchErrorMessage,
  launchMediaFromDraft,
  launchSteps,
  maximumLaunchStep,
  normalizePlacementTransform,
  readLaunchDraft,
  writeLaunchDraft,
} from './launchWorkflow';

const offerPlatformLabel = (item) => labelize(item?.provider) || 'Fulfillment platform';

const offerProviderLabel = (item) => {
  const externalProviderID = String(item?.external_provider_id || '').trim();
  if (externalProviderID) {
    return `${String(item?.provider || '').toLowerCase() === 'printify' ? 'Print provider' : 'Provider'} #${externalProviderID}`;
  }
  const manufacturer = [item?.brand, item?.model].filter(Boolean).join(' ').trim();
  return manufacturer || item?.connection?.display_name || 'Default provider';
};

const offerSelectLabel = (item) => `${offerPlatformLabel(item)} · ${offerProviderLabel(item)}`;

const offerDetailLabel = (item) => [
  item?.title,
  [item?.brand, item?.model].filter(Boolean).join(' ').trim(),
  item?.connection?.display_name,
].filter(Boolean).join(' · ');

const mockupKey = (item) => String(item?.id || item?.image_id || item?.source_url || '').trim();

const normalizeLaunchMockup = (item) => ({
  id: mockupKey(item),
  image_id: String(item?.image_id || '').trim() || null,
  source_url: String(item?.source_url || '').trim(),
  preview_url: String(item?.preview_url || item?.source_url || '').trim(),
  media_type: String(item?.media_type || 'image').trim().toLowerCase() || 'image',
  alt_text: String(item?.alt_text || '').trim(),
  source: String(item?.source || 'provider').trim(),
  provider: String(item?.provider || '').trim(),
  offer_id: String(item?.offer_id || '').trim(),
  filename: String(item?.filename || '').trim(),
});

const absoluteAPIURL = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  try {
    return new URL(raw, `${config.apiBaseUrl}/`).toString();
  } catch {
    return raw;
  }
};

const CanonicalLaunchWizard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'));
  const { fetchWithAuth } = useAuth();
  const { flags } = useFeatureFlags();
  const [searchParams, setSearchParams] = useSearchParams();
  const [draft, setDraft] = React.useState(readLaunchDraft);
  const [catalogDetail, setCatalogDetail] = React.useState(null);
  const [designDetail, setDesignDetail] = React.useState(null);
  const [product, setProduct] = React.useState(null);
  const [listing, setListing] = React.useState(null);
  const [productValidation, setProductValidation] = React.useState(null);
  const [listingValidation, setListingValidation] = React.useState(null);
  const [busy, setBusy] = React.useState('');
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');
  const [mockupFile, setMockupFile] = React.useState(null);
  const [uploadingMockup, setUploadingMockup] = React.useState(false);
  const [mockupUploadError, setMockupUploadError] = React.useState('');
  const [createPublicationOpen, setCreatePublicationOpen] = React.useState(false);
  const [managedPublication, setManagedPublication] = React.useState(null);
  const stepRegionRef = React.useRef(null);
  const mockupInputRef = React.useRef(null);

  const maxStep = maximumLaunchStep(draft);
  const requestedStep = Number.parseInt(searchParams.get('step') || '', 10);
  const desiredStep = Number.isInteger(requestedStep) && requestedStep >= 0 && requestedStep < launchSteps.length
    ? requestedStep
    : Math.min(Math.max(Number(draft.step || 0), 0), launchSteps.length - 1);
  const currentStep = Math.min(desiredStep, maxStep);
  const resumeProductID = searchParams.get('product_id') || '';

  React.useEffect(() => {
    writeLaunchDraft(draft);
  }, [draft]);

  React.useEffect(() => {
    const handle = window.requestAnimationFrame(() => stepRegionRef.current?.focus());
    return () => window.cancelAnimationFrame(handle);
  }, [currentStep]);

  const patchDraft = React.useCallback((patch) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const goToStep = React.useCallback((nextStep) => {
    const normalized = Math.min(Math.max(nextStep, 0), launchSteps.length - 1);
    patchDraft({ step: normalized });
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('step', String(normalized));
      return next;
    }, { replace: true });
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: reduceMotion ? 'auto' : 'smooth' });
  }, [patchDraft, setSearchParams]);

  const loadProduct = React.useCallback(async (productID = draft.productId) => {
    if (!productID) {
      setProduct(null);
      return null;
    }
    const payload = await canonicalRequest(fetchWithAuth, `/products/${productID}`);
    setProduct(payload.item);
    return payload.item;
  }, [draft.productId, fetchWithAuth]);

  const loadListing = React.useCallback(async (listingID = draft.listingId) => {
    if (!listingID) {
      setListing(null);
      return null;
    }
    const payload = await canonicalRequest(fetchWithAuth, `/listings/${listingID}`);
    setListing(payload.item);
    return payload.item;
  }, [draft.listingId, fetchWithAuth]);

  React.useEffect(() => {
    if (!draft.productId) return;
    loadProduct().catch((loadError) => setError(loadError.message || 'Unable to load the saved Product draft.'));
  }, [draft.productId, loadProduct]);

  React.useEffect(() => {
    if (!draft.listingId) return;
    loadListing().catch((loadError) => setError(loadError.message || 'Unable to load the saved Listing draft.'));
  }, [draft.listingId, loadListing]);

  React.useEffect(() => {
    if (!draft.template?.id) {
      setCatalogDetail(null);
      return;
    }
    let active = true;
    canonicalRequest(fetchWithAuth, `/catalog/${draft.template.id}`)
      .then((payload) => {
        if (!active) return;
        const item = payload.item;
        setCatalogDetail(item);
        setDraft((current) => {
          if (current.template?.id !== item?.id || current.descriptionTouched) return current;
          const description = String(item?.description || '');
          return current.description === description ? current : { ...current, description };
        });
      })
      .catch((loadError) => active && setError(loadError.message || 'Unable to load catalog details.'));
    return () => { active = false; };
  }, [draft.template?.id, fetchWithAuth]);

  React.useEffect(() => {
    if (!draft.design?.id) {
      setDesignDetail(null);
      return;
    }
    let active = true;
    canonicalRequest(fetchWithAuth, `/designs/${draft.design.id}`)
      .then((payload) => active && setDesignDetail(payload.item))
      .catch((loadError) => active && setError(loadError.message || 'Unable to load design details.'));
    return () => { active = false; };
  }, [draft.design?.id, fetchWithAuth]);

  React.useEffect(() => {
    if (!resumeProductID || draft.productId) return;
    let active = true;
    setBusy('resume');
    canonicalRequest(fetchWithAuth, `/products/${resumeProductID}`)
      .then((payload) => {
        if (!active) return;
        const item = payload.item;
        setProduct(item);
        setDraft((current) => ({
          ...current,
          productId: item.id,
          design: item.design || current.design,
          template: item.catalog_template || current.template,
          offerId: item.default_fulfillment_offer?.id || current.offerId,
          productName: item.name || current.productName,
          variantIDs: (item.variants || []).map((variant) => variant.fulfillment_offer_variant_id).filter(Boolean),
          placementKeys: Array.from(new Set((item.placements || []).map((placement) => placement.placement_key).filter(Boolean))),
          placementTransforms: (item.placements || [])
            .filter((placement) => !placement.product_variant_id)
            .reduce((result, placement) => {
              // The initial editor controls the first/base layer while
              // preserving any additional layers already on the Product.
              if (!result[placement.placement_key]) result[placement.placement_key] = normalizePlacementTransform(placement.transform);
              return result;
            }, {}),
          listingTitle: current.listingTitle || item.name || '',
          step: Math.max(current.step || 0, 3),
        }));
      })
      .catch((loadError) => active && setError(loadError.message || 'Unable to resume this Product.'))
      .finally(() => active && setBusy(''));
    return () => { active = false; };
  }, [draft.productId, fetchWithAuth, resumeProductID]);

  const offer = (catalogDetail?.offers || []).find((item) => item.id === draft.offerId) || null;
  const publications = listing?.publications || [];
  const providerMockups = (catalogDetail?.mockups || []).filter((item) => !item.offer_id || item.offer_id === draft.offerId);
  const mockupChoices = (() => {
    const result = [];
    const seen = new Set();
    [...providerMockups, ...(draft.uploadedMockups || [])].forEach((item) => {
      const normalized = normalizeLaunchMockup(item);
      const key = mockupKey(normalized);
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push(normalized);
    });
    return result;
  })();
  const selectDesign = (design) => {
    const suggested = defaultProductName(design, draft.template);
    setDesignDetail(null);
    patchDraft({
      design,
      productName: draft.productName || suggested,
      listingTitle: draft.listingTitle || suggested,
      plans: {},
      workflows: {},
    });
  };

  const selectTemplate = (template) => {
    const suggested = defaultProductName(draft.design, template);
    setCatalogDetail(null);
    patchDraft({
      template,
      offerId: '',
      variantIDs: [],
      placementKeys: [],
      placementTransforms: {},
      selectedMockups: [],
      uploadedMockups: [],
      mediaText: '',
      description: draft.descriptionTouched ? draft.description : '',
      productName: draft.productName || suggested,
      listingTitle: draft.listingTitle || suggested,
      plans: {},
      workflows: {},
    });
  };

  const selectOffer = (offerID) => {
    const selected = (catalogDetail?.offers || []).find((item) => item.id === offerID);
    setDraft((current) => {
      const selectedMockups = (current.selectedMockups || []).filter((item) => item.source !== 'provider');
      return {
        ...current,
        offerId: offerID,
        variantIDs: (selected?.variants || []).filter((item) => item.available !== false).map((item) => item.id),
        placementKeys: (selected?.placements || []).map((item) => item.placement_key),
        placementTransforms: Object.fromEntries((selected?.placements || []).map((item) => [
          item.placement_key,
          normalizePlacementTransform(item.default_transform),
        ])),
        selectedMockups,
        plans: {},
        workflows: {},
      };
    });
  };

  const toggleMockup = (item) => {
    const normalized = normalizeLaunchMockup(item);
    const key = mockupKey(normalized);
    if (!key) return;
    setDraft((current) => {
      const selected = [...(current.selectedMockups || [])];
      const index = selected.findIndex((candidate) => mockupKey(candidate) === key);
      if (index >= 0) selected.splice(index, 1); else selected.push(normalized);
      return { ...current, selectedMockups: selected, plans: {}, workflows: {} };
    });
  };

  const uploadMockup = async () => {
    if (!mockupFile) {
      setMockupUploadError('Choose an image to upload.');
      return;
    }
    if (!String(mockupFile.type || '').startsWith('image/')) {
      setMockupUploadError('The selected file must be an image.');
      return;
    }
    if (mockupFile.size > 25 * 1024 * 1024) {
      setMockupUploadError('Choose an image smaller than 25 MB.');
      return;
    }
    setUploadingMockup(true);
    setMockupUploadError('');
    try {
      const extensionMatch = mockupFile.name.match(/(\.[a-z0-9]+)$/i);
      const extension = extensionMatch?.[1] || '';
      const stem = mockupFile.name.slice(0, extension ? -extension.length : undefined).replace(/[^a-z0-9_-]+/gi, '-').replace(/^-|-$/g, '').slice(0, 72) || 'image';
      const uniqueID = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const form = new FormData();
      form.append('file', mockupFile, `mockup-${uniqueID}-${stem}${extension}`);
      form.append('options', JSON.stringify({
        upload_printful: false,
        upload_printify: false,
        upscale_image: false,
      }));
      const response = await fetchWithAuth(`${config.apiBaseUrl}/images`, { method: 'POST', body: form });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || `Upload failed with status ${response.status}`);
      const sourceURL = absoluteAPIURL(payload.printful_url || payload.image_url || payload.original_url);
      if (!payload.id || !sourceURL) throw new Error('The upload completed without a durable image URL.');
      const uploaded = normalizeLaunchMockup({
        id: `asset:${payload.id}`,
        image_id: payload.id,
        source_url: sourceURL,
        preview_url: absoluteAPIURL(payload.thumbnail_url || payload.original_url || sourceURL),
        alt_text: mockupFile.name,
        media_type: 'image',
        source: 'upload',
        filename: mockupFile.name,
      });
      setDraft((current) => ({
        ...current,
        uploadedMockups: [...(current.uploadedMockups || []).filter((item) => mockupKey(item) !== uploaded.id), uploaded],
        selectedMockups: [...(current.selectedMockups || []).filter((item) => mockupKey(item) !== uploaded.id), uploaded],
        plans: {},
        workflows: {},
      }));
      setMockupFile(null);
      if (mockupInputRef.current) mockupInputRef.current.value = '';
      setNotice('Mockup uploaded and added to the end of the selection.');
    } catch (uploadError) {
      setMockupUploadError(uploadError.message || 'Unable to upload this mockup.');
    } finally {
      setUploadingMockup(false);
    }
  };

  const toggleVariant = (variantID) => {
    const selected = new Set(draft.variantIDs || []);
    if (selected.has(variantID)) selected.delete(variantID); else selected.add(variantID);
    patchDraft({ variantIDs: Array.from(selected), plans: {}, workflows: {} });
  };

  const togglePlacement = (placementKey) => {
    const placement = (offer?.placements || []).find((item) => item.placement_key === placementKey);
    setDraft((current) => {
      const selected = new Set(current.placementKeys || []);
      const transforms = { ...(current.placementTransforms || {}) };
      if (selected.has(placementKey)) {
        selected.delete(placementKey);
      } else {
        selected.add(placementKey);
        if (!transforms[placementKey]) transforms[placementKey] = normalizePlacementTransform(placement?.default_transform);
      }
      return { ...current, placementKeys: Array.from(selected), placementTransforms: transforms, plans: {}, workflows: {} };
    });
  };

  const updatePlacementTransform = (placementKey, transform) => {
    setDraft((current) => ({
      ...current,
      placementTransforms: {
        ...(current.placementTransforms || {}),
        [placementKey]: normalizePlacementTransform(transform),
      },
      plans: {},
      workflows: {},
    }));
  };

  const saveProduct = async () => {
    setBusy('product');
    setError('');
    setNotice('');
    try {
      const current = draft.productId ? (product || await loadProduct()) : null;
      const payload = buildLaunchProductPayload({ draft, catalogDetail, designDetail, currentProduct: current });
      const result = await canonicalRequest(fetchWithAuth, current ? `/products/${current.id}` : '/products', {
        method: current ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
      });
      const productID = current?.id || result.id;
      patchDraft({ productId: productID, plans: {}, workflows: {} });
      await loadProduct(productID);
      setProductValidation(result.validation || null);
      setNotice('Product draft saved. No provider was changed.');
      return productID;
    } catch (saveError) {
      setProductValidation(saveError.validation || null);
      setError(saveError.status === 409 ? 'The Product changed in another session. Reload it before continuing.' : saveError.message || 'Unable to save the Product draft.');
      throw saveError;
    } finally {
      setBusy('');
    }
  };

  const persistListing = async (desiredStatus) => {
    const currentProduct = await loadProduct();
    const currentListing = draft.listingId ? await loadListing() : null;
    const payload = buildLaunchListingPayload({
      draft,
      product: currentProduct,
      currentListing,
      desiredStatus,
    });
    const result = await canonicalRequest(fetchWithAuth, currentListing ? `/listings/${currentListing.id}` : '/listings', {
      method: currentListing ? 'PATCH' : 'POST',
      body: JSON.stringify(payload),
    });
    const listingID = currentListing?.id || result.id;
    patchDraft({ listingId: listingID, plans: {}, workflows: {} });
    const savedListing = await loadListing(listingID);
    setListingValidation(result.validation || null);
    return savedListing;
  };

  const saveListing = async () => {
    setBusy('listing');
    setError('');
    setNotice('');
    try {
      const savedListing = await persistListing('draft');
      setNotice('Listing draft saved locally. No sales channel was changed.');
      return savedListing.id;
    } catch (saveError) {
      setListingValidation(saveError.validation || null);
      setError(saveError.status === 409
        ? 'The Listing changed in another session. Reload it before continuing.'
        : launchErrorMessage(saveError, 'Unable to save the Listing draft.'));
      throw saveError;
    } finally {
      setBusy('');
    }
  };

  const validateDrafts = React.useCallback(async () => {
    if (!draft.productId || !draft.listingId) return;
    const [productResult, listingResult] = await Promise.allSettled([
      canonicalRequest(fetchWithAuth, `/products/${draft.productId}/validate`, { method: 'POST' }),
      canonicalRequest(fetchWithAuth, `/listings/${draft.listingId}/validate`, { method: 'POST' }),
    ]);
    if (productResult.status === 'fulfilled') setProductValidation(productResult.value);
    if (listingResult.status === 'fulfilled') setListingValidation(listingResult.value);
  }, [draft.listingId, draft.productId, fetchWithAuth]);

  React.useEffect(() => {
    if (currentStep === 6) validateDrafts();
  }, [currentStep, validateDrafts]);

  const previewPlans = async () => {
    setBusy('plans');
    setError('');
    setNotice('');
    try {
      // Persist the current browser draft even when the stored Listing was already
      // ready. The non-linear stepper lets a user revisit fields before planning.
      const currentListing = await persistListing('ready');
      const destinations = currentListing?.publications || [];
      if (!destinations.length) {
        setError('Add at least one sales channel before previewing publication plans.');
        return;
      }
      const nextPlans = {};
      for (const publication of destinations) {
        try {
          const plan = await canonicalRequest(fetchWithAuth, `/publications/${publication.id}/plan`, {
            method: 'POST',
            body: JSON.stringify({ expires_in_seconds: 1800 }),
          });
          nextPlans[publication.id] = { status: 'planned', plan };
        } catch (planError) {
          nextPlans[publication.id] = {
            status: 'failed',
            error: launchErrorMessage(planError, 'Planning failed.'),
            validation: planError.validation || null,
          };
        }
        patchDraft({ plans: { ...nextPlans }, workflows: {} });
      }
      const plannedCount = Object.values(nextPlans).filter((entry) => entry.status === 'planned').length;
      const failedCount = destinations.length - plannedCount;
      if (plannedCount) {
        setNotice(`${plannedCount} publication plan${plannedCount === 1 ? '' : 's'} ready.${failedCount ? ` ${failedCount} destination${failedCount === 1 ? '' : 's'} need attention.` : ' Review every action before continuing.'}`);
      } else {
        setError('No publication plans are ready. Review the destination-specific issues below.');
      }
    } catch (planError) {
      setListingValidation(planError.validation || null);
      setError(planError.status === 409
        ? 'The Listing changed in another session. Reload it before previewing plans.'
        : launchErrorMessage(planError, 'Unable to prepare the Listing for planning.'));
    } finally {
      setBusy('');
    }
  };

  const applyPlans = async () => {
    const planned = Object.entries(draft.plans || {}).filter(([, entry]) => entry.status === 'planned' && entry.plan?.id && entry.plan?.plan_hash);
    if (!planned.length) {
      setError('Preview a valid publication plan before publishing.');
      return;
    }
    setBusy('publish');
    setError('');
    const nextWorkflows = { ...(draft.workflows || {}) };
    for (const [publicationID, entry] of planned) {
      nextWorkflows[publicationID] = { status: 'starting' };
      patchDraft({ workflows: { ...nextWorkflows } });
      try {
        const workflow = await canonicalRequest(fetchWithAuth, `/publication-plans/${entry.plan.id}/apply`, {
          method: 'POST',
          body: JSON.stringify({ plan_hash: entry.plan.plan_hash }),
        });
        nextWorkflows[publicationID] = { status: 'started', workflow };
      } catch (applyError) {
        nextWorkflows[publicationID] = { status: 'failed', error: applyError.message || 'Unable to start workflow.' };
      }
      patchDraft({ workflows: { ...nextWorkflows } });
    }
    setBusy('');
    setNotice('Every accepted plan now has a durable workflow or a visible error. Provider work continues in Activity.');
    await loadListing();
  };

  const reset = () => {
    if (!window.confirm('Start a new launch? Existing server-side Product and Listing drafts will be kept.')) return;
    const next = emptyLaunchDraft();
    setDraft(next);
    setProduct(null);
    setListing(null);
    setCatalogDetail(null);
    setDesignDetail(null);
    setProductValidation(null);
    setListingValidation(null);
    setMockupFile(null);
    setMockupUploadError('');
    if (mockupInputRef.current) mockupInputRef.current.value = '';
    setError('');
    setNotice('New launch draft started.');
    setSearchParams({ step: '0' }, { replace: true });
  };

  const advance = async () => {
    try {
      if (currentStep === 2) await saveProduct();
      if (currentStep === 4) await saveListing();
      goToStep(currentStep + 1);
    } catch {
      // The step remains open with a precise validation or request error.
    }
  };

  const canAdvance = (() => {
    if (busy || uploadingMockup) return false;
    if (currentStep === 0) return Boolean(draft.design?.id);
    if (currentStep === 1) return Boolean(draft.template?.id && draft.offerId && offer);
    if (currentStep === 2) return Boolean(draft.productName.trim() && designDetail?.image_id && draft.variantIDs.length && draft.placementKeys.length);
    if (currentStep === 4) return Boolean(draft.productId);
    if (currentStep === 5) return Boolean(draft.listingId);
    if (currentStep === 6) return publications.length > 0 && Object.values(draft.plans || {}).some((entry) => entry.status === 'planned');
    return currentStep < launchSteps.length - 1;
  })();

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <SectionCard title="Choose a design" description="Start with reusable artwork. This choice stays independent from fulfillment and sales channels.">
            <RemoteEntityGalleryPicker
              fetchWithAuth={fetchWithAuth}
              endpoint="/designs"
              label="Design"
              value={draft.design}
              onChange={selectDesign}
              getItemTitle={(item) => item.filename}
              getItemSubtitle={(item) => [item.width && item.height ? `${item.width} × ${item.height}` : '', labelize(item.status)].filter(Boolean).join(' · ')}
              getItemImage={(item) => item.image_url}
              helperText="Search by filename, then choose the artwork you want to place on this product."
              emptyMessage="No designs match this search."
            />
          </SectionCard>
        );
      case 1:
        return (
          <SectionCard title="Choose a product type and fulfillment offer" description="PODBoss keeps the product concept stable while an offer identifies who can manufacture it.">
            <RemoteEntityGalleryPicker
              fetchWithAuth={fetchWithAuth}
              endpoint="/catalog"
              label="Product type"
              value={draft.template}
              onChange={selectTemplate}
              getItemTitle={(item) => item.name}
              getItemSubtitle={(item) => item.product_type || `${(item.offers || []).length} fulfillment offer${(item.offers || []).length === 1 ? '' : 's'}`}
              getItemImage={(item) => item.default_image_url}
              renderItemMeta={(item) => (
                <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                  {Array.from(new Set((item.offers || []).map((itemOffer) => offerPlatformLabel(itemOffer)))).slice(0, 2).map((platform) => (
                    <Chip key={platform} size="small" variant="outlined" label={platform} sx={{ height: 22 }} />
                  ))}
                  <Typography variant="caption" color="text.secondary">{(item.offers || []).length} offer{(item.offers || []).length === 1 ? '' : 's'}</Typography>
                </Stack>
              )}
              helperText="Search the provider-neutral catalog, then choose the physical product you want to create."
              emptyMessage="No product types match this search."
            />
            <TextField
              select
              label="Fulfillment offer"
              value={draft.offerId}
              onChange={(event) => selectOffer(event.target.value)}
              disabled={!catalogDetail}
              required
              helperText={draft.template && !catalogDetail ? 'Loading fulfillment options…' : 'The platform and print provider combination that will manufacture this product.'}
              SelectProps={{
                renderValue: (selectedID) => {
                  const selected = (catalogDetail?.offers || []).find((item) => item.id === selectedID);
                  return selected ? `${offerSelectLabel(selected)} — ${selected.title}` : 'Choose an offer';
                },
              }}
            >
              <MenuItem value=""><em>Choose an offer</em></MenuItem>
              {(catalogDetail?.offers || []).map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Box sx={{ py: 0.25, minWidth: 0 }}>
                    <Typography variant="body2">{offerSelectLabel(item)}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{offerDetailLabel(item)}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
            {offer ? <EntityStatGrid items={[
              { label: 'Platform', value: offerPlatformLabel(offer), helper: offer.connection?.display_name },
              { label: 'Print provider', value: offerProviderLabel(offer), helper: [offer.brand, offer.model].filter(Boolean).join(' ') },
              { label: 'Variants', value: (offer.variants || []).length, helper: `${(offer.variants || []).filter((item) => item.available !== false).length} available` },
              { label: 'Placements', value: (offer.placements || []).length },
            ]} /> : null}
          </SectionCard>
        );
      case 2:
        return (
          <Stack spacing={2}>
            <SectionCard title="Name the Product" description="This is the stable PODBoss identity; provider copies remain fulfillment instances.">
              <TextField fullWidth required label="Product name" value={draft.productName} onChange={(event) => patchDraft({ productName: event.target.value, listingTitle: draft.listingTitle || event.target.value, plans: {}, workflows: {} })} />
            </SectionCard>
            <SectionCard
              title="Choose variants"
              description="Only selected, available variants will become part of the Product."
              action={<Button size="small" onClick={() => patchDraft({ variantIDs: draft.variantIDs.length === (offer?.variants || []).filter((item) => item.available !== false).length ? [] : (offer?.variants || []).filter((item) => item.available !== false).map((item) => item.id) })}>{draft.variantIDs.length ? 'Toggle all' : 'Select all'}</Button>}
            >
              <Box sx={{ maxHeight: 420, overflow: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 0.5 }}>
                {(offer?.variants || []).map((variant) => (
                  <FormControlLabel
                    key={variant.id}
                    disabled={variant.available === false}
                    control={<Checkbox checked={draft.variantIDs.includes(variant.id)} onChange={() => toggleVariant(variant.id)} />}
                    label={<Box><Typography variant="body2">{variant.title || variant.external_id}</Typography><Typography variant="caption" color="text.secondary">{variant.currency} {(Number(variant.cost || 0) / 100).toFixed(2)} cost</Typography></Box>}
                  />
                ))}
              </Box>
            </SectionCard>
            <SectionCard title="Place your artwork" description="Position artwork once in a normalized print area. Fulfillment adapters translate it to each provider and variant canvas.">
              <ProductPlacementDesigner
                placements={offer?.placements || []}
                selectedKeys={draft.placementKeys || []}
                transforms={draft.placementTransforms || {}}
                variants={offer?.variants || []}
                selectedVariantIDs={draft.variantIDs || []}
                artwork={{
                  src: designDetail?.image_url || draft.design?.image_url,
                  width: designDetail?.width || draft.design?.width,
                  height: designDetail?.height || draft.design?.height,
                  name: designDetail?.filename || draft.design?.filename,
                }}
                productImageURL={firstOfferPreviewImage(catalogDetail, offer?.id)}
                onToggle={togglePlacement}
                onTransformChange={updatePlacementTransform}
              />
              <ValidationSummary validation={productValidation} title="Product draft" />
            </SectionCard>
          </Stack>
        );
      case 3: {
        const selectedMedia = launchMediaFromDraft(draft);
        return (
          <Stack spacing={2}>
            <SectionCard
              title="Choose provider mockups"
              description="PODBoss presents every fulfillment platform through the same mockup interface. Select images in listing order; selection 1 is featured."
              action={(draft.selectedMockups || []).length ? <Button size="small" onClick={() => patchDraft({ selectedMockups: [], plans: {}, workflows: {} })}>Clear selection</Button> : null}
            >
              <MediaGalleryPicker
                items={mockupChoices}
                selectedItems={draft.selectedMockups || []}
                onToggle={toggleMockup}
                getItemID={mockupKey}
                getItemTitle={(item, index) => item.filename || item.alt_text || `Mockup ${index + 1}`}
                getItemSubtitle={(item) => item.source === 'upload' ? 'Uploaded mockup' : `${labelize(item.provider) || 'Provider'} catalog`}
                emptyMessage="This offer does not have provider mockups yet. Upload your own image below."
                disabled={uploadingMockup}
              />
            </SectionCard>

            <SectionCard title="Upload another mockup" description="Upload a custom image to PODBoss. It is added to this gallery and selected automatically, without uploading it to a fulfillment provider.">
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
                  <Button component="label" variant="outlined" startIcon={<CloudUploadOutlinedIcon />} disabled={uploadingMockup}>
                    Choose image
                    <Box
                      ref={mockupInputRef}
                      component="input"
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(event) => {
                        setMockupFile(event.target.files?.[0] || null);
                        setMockupUploadError('');
                      }}
                    />
                  </Button>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap title={mockupFile?.name || ''}>{mockupFile?.name || 'No image selected'}</Typography>
                    <Typography variant="caption" color="text.secondary">PNG, JPEG, GIF, or WebP · maximum 25 MB</Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={uploadMockup}
                    disabled={!mockupFile || uploadingMockup}
                    startIcon={uploadingMockup ? <CircularProgress size={16} color="inherit" /> : <CloudUploadOutlinedIcon />}
                  >
                    {uploadingMockup ? 'Uploading…' : 'Upload and select'}
                  </Button>
                </Stack>
              </Paper>
              {mockupUploadError ? <Alert severity="error">{mockupUploadError}</Alert> : null}
            </SectionCard>

            <SectionCard title="Add mockups by URL" description="Optional: add an existing durable image URL when the mockup is already hosted elsewhere.">
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Mockup image URLs"
                value={draft.mediaText}
                onChange={(event) => patchDraft({ mediaText: event.target.value, plans: {}, workflows: {} })}
                helperText="Enter one URL per line. URL images appear after the gallery selections."
              />
            </SectionCard>

            {selectedMedia.length ? (
              <Alert severity="success">{selectedMedia.length} mockup{selectedMedia.length === 1 ? '' : 's'} will be saved. The first is the featured image.</Alert>
            ) : <Alert severity="info">Mockups are optional while drafting, but required before a Listing can be marked ready.</Alert>}
          </Stack>
        );
      }
      case 4:
        return (
          <Stack spacing={2}>
            <SectionCard title="Prepare reusable listing content" description="Channel-specific wording belongs in Publication overrides; this content is the shared default.">
              <TextField required label="Listing title" value={draft.listingTitle} onChange={(event) => patchDraft({ listingTitle: event.target.value, plans: {}, workflows: {} })} />
              <TextField
                multiline
                minRows={8}
                label="Description"
                value={draft.description}
                onChange={(event) => patchDraft({ description: event.target.value, descriptionTouched: true, plans: {}, workflows: {} })}
                helperText={catalogDetail?.description && !draft.descriptionTouched ? 'Filled from the selected catalog product. You can edit it for this Listing.' : 'Shared listing description; channel-specific wording can be added later.'}
              />
              <TextField multiline minRows={2} label="Tags" value={draft.tagsText} onChange={(event) => patchDraft({ tagsText: event.target.value, plans: {}, workflows: {} })} helperText="Separate tags with commas or new lines." />
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 1.5 }}>
                <TextField label="Language" value={draft.language} onChange={(event) => patchDraft({ language: event.target.value })} />
                <TextField label="Currency" value={draft.currency} onChange={(event) => patchDraft({ currency: event.target.value.toUpperCase(), plans: {}, workflows: {} })} inputProps={{ maxLength: 3 }} />
                <TextField label={`Price for all variants (${draft.currency})`} value={draft.priceText} onChange={(event) => patchDraft({ priceText: event.target.value, plans: {}, workflows: {} })} inputMode="decimal" />
              </Box>
            </SectionCard>
            <ValidationSummary validation={listingValidation} title="Listing draft" />
          </Stack>
        );
      case 5:
        return (
          <SectionCard title="Choose sales channels" description="Adding a destination is local. No provider changes until you review and apply an immutable plan." action={<Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreatePublicationOpen(true)} disabled={!draft.listingId}>Add channel</Button>}>
            {!draft.listingId ? <Alert severity="info">Save the Listing before adding channels.</Alert> : null}
            {publications.length ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                {publications.map((publication) => <PublicationCard key={publication.id} publication={publication} onManage={setManagedPublication} />)}
              </Box>
            ) : <Alert severity="info">No destination has been selected yet. You may continue saving this as an unpublished Listing draft.</Alert>}
          </SectionCard>
        );
      case 6: {
        const listingReady = listing?.status === 'ready';
        return (
          <Stack spacing={2}>
            <SectionCard title="Review readiness" description="Validation is local and cannot modify an external provider.">
              <Alert severity={listingReady ? 'success' : 'info'}>
                {listingReady
                  ? 'This Listing is ready for publication planning.'
                  : 'This Listing is still a draft. Previewing plans will first validate it and mark it ready locally; no external provider will be changed.'}
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                <Box><Typography variant="subtitle2" gutterBottom>Product</Typography><ValidationSummary validation={productValidation} title="Product readiness" /></Box>
                <Box><Typography variant="subtitle2" gutterBottom>Listing</Typography><ValidationSummary validation={listingValidation} title="Listing readiness" /></Box>
              </Box>
            </SectionCard>
            <SectionCard
              title="Immutable publication plans"
              description="Preview every intended provider action. Planning writes only an expiring local plan."
              action={(
                <Button variant="contained" onClick={previewPlans} disabled={!flags.publication_workflows || busy === 'plans' || !publications.length}>
                  {busy === 'plans' ? (listingReady ? 'Planning…' : 'Checking readiness…') : (listingReady ? 'Preview all plans' : 'Mark ready & preview plans')}
                </Button>
              )}
            >
              {!flags.publication_workflows ? <Alert severity="warning">Publication workflows are disabled for this environment.</Alert> : null}
              {publications.map((publication) => {
                const entry = draft.plans?.[publication.id];
                return (
                  <Paper key={publication.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">{publication.connection?.display_name || labelize(publication.provider)}</Typography>
                        <StatusChip status={entry?.status || 'draft'} label={entry?.status === 'planned' ? 'Plan ready' : entry?.status === 'failed' ? 'Plan failed' : 'Not planned'} />
                      </Stack>
                      {entry?.validation ? (
                        <ValidationSummary validation={entry.validation} title={`${publication.connection?.display_name || labelize(publication.provider)} plan`} />
                      ) : entry?.error ? <Alert severity="error">{entry.error}</Alert> : null}
                      {entry?.plan ? (
                        <>
                          <Typography variant="caption" color="text.secondary">Plan {entry.plan.plan_hash?.slice(0, 12)}… expires {formatDate(entry.plan.expires_at)}</Typography>
                          {(entry.plan.warnings || []).map((warning) => <Alert key={`${warning.code}-${warning.field}`} severity="warning">{warning.message}</Alert>)}
                          <Box component="ol" sx={{ my: 0, pl: 3 }}>
                            {(entry.plan.plan?.actions || []).map((action) => <li key={action.key}><Typography variant="body2">{action.description}</Typography></li>)}
                          </Box>
                        </>
                      ) : <Typography variant="body2" color="text.secondary">Preview this destination to see its exact scope.</Typography>}
                    </Stack>
                  </Paper>
                );
              })}
            </SectionCard>
          </Stack>
        );
      }
      case 7: {
        const workflowEntries = draft.workflows || {};
        const started = Object.values(workflowEntries).filter((item) => item.status === 'started').length;
        const failed = Object.values(workflowEntries).filter((item) => item.status === 'failed').length;
        return (
          <SectionCard
            title="Publish and monitor"
            description="Starting creates durable workflows. Provider requests are throttled, retried when safe, checkpointed, and remotely verified."
            action={<Button variant="contained" startIcon={<LaunchIcon />} onClick={applyPlans} disabled={busy === 'publish' || !Object.values(draft.plans || {}).some((entry) => entry.status === 'planned')}>{busy === 'publish' ? 'Starting…' : 'Start publication'}</Button>}
          >
            <EntityStatGrid items={[
              { label: 'Plans ready', value: Object.values(draft.plans || {}).filter((item) => item.status === 'planned').length },
              { label: 'Workflows started', value: started },
              { label: 'Start failures', value: failed },
              { label: 'Destinations', value: publications.length },
            ]} />
            {publications.map((publication) => {
              const entry = workflowEntries[publication.id];
              return (
                <Paper key={publication.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <Box>
                      <Typography variant="subtitle2">{publication.connection?.display_name || labelize(publication.provider)}</Typography>
                      {entry?.error ? <Typography variant="body2" color="error">{entry.error}</Typography> : <Typography variant="body2" color="text.secondary">{entry?.workflow ? `Workflow ${entry.workflow.workflow_run_id}` : 'Not started yet'}</Typography>}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <StatusChip status={entry?.status || 'planned'} />
                      {entry?.workflow?.workflow_run_id ? <Button endIcon={<OpenInNewIcon />} onClick={() => navigate(`/activity?kind=workflow&q=${entry.workflow.workflow_run_id}`)}>Activity</Button> : null}
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
            {started && !failed ? <Alert severity="success" icon={<CheckCircleIcon />}>All publication workflows were accepted. You can safely leave this page and monitor eventual completion in Activity.</Alert> : null}
          </SectionCard>
        );
      }
      default:
        return null;
    }
  };

  const nextLabel = currentStep === 2 ? 'Save product & continue' : currentStep === 4 ? 'Save listing & continue' : currentStep === 6 ? 'Continue to publish' : 'Continue';

  return (
    <Stack spacing={2.5} sx={{ pb: 12 }}>
      <WorkspacePageHeader
        eyebrow="Guided workflow"
        title="Launch a Product"
        description="Create reusable commerce intent first, then preview and safely materialize it across providers. Progress is saved in this browser and in canonical drafts."
        breadcrumbs={[{ label: 'Products', to: '/products' }, { label: 'Launch a Product' }]}
        badges={<><Chip size="small" variant="outlined" color="primary" label="Resumable draft" />{draft.productId ? <Chip size="small" variant="outlined" label="Product saved" /> : null}{draft.listingId ? <Chip size="small" variant="outlined" label="Listing saved" /> : null}</>}
        actions={(
          <>
            {draft.productId ? <Button variant="outlined" onClick={() => navigate(`/products/${draft.productId}`)}>Open Product</Button> : null}
            {draft.listingId ? <Button variant="outlined" onClick={() => navigate(`/listings/${draft.listingId}`)}>Open Listing</Button> : null}
            <Button color="inherit" startIcon={<DeleteSweepIcon />} onClick={reset}>Start another</Button>
          </>
        )}
      />

      {busy === 'resume' ? <Alert severity="info" icon={<CircularProgress size={18} />}>Loading the existing Product into this launch…</Alert> : null}
      {error ? <Alert severity="error" role="alert">{error}</Alert> : null}
      {notice ? <Alert severity="success" role="status" aria-live="polite">{notice}</Alert> : null}

      <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
        <Stepper nonLinear activeStep={currentStep} orientation={mobile ? 'vertical' : 'horizontal'}>
          {launchSteps.map((label, index) => (
            <Step key={label} completed={index < currentStep && index <= maxStep} disabled={index > maxStep}>
              <StepButton onClick={() => index <= maxStep && goToStep(index)}>{label}</StepButton>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Box ref={stepRegionRef} component="section" tabIndex={-1} aria-label={`${launchSteps[currentStep]} step`} sx={{ '&:focus': { outline: 'none' } }}>
        {renderStep()}
      </Box>

      <Paper
        variant="outlined"
        sx={{ position: 'relative', mx: { xs: 0.25, md: 0 }, p: 1.25, bgcolor: 'background.paper' }}
      >
        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Button startIcon={<ArrowBackIcon />} onClick={() => goToStep(currentStep - 1)} disabled={currentStep === 0 || Boolean(busy)}>Back</Button>
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Step {currentStep + 1} of {launchSteps.length} · local progress saved automatically</Typography>
          {currentStep < launchSteps.length - 1 ? (
            <Button variant="contained" endIcon={busy ? <CircularProgress size={16} color="inherit" /> : <ArrowForwardIcon />} onClick={advance} disabled={!canAdvance}>
              {busy ? 'Saving…' : nextLabel}
            </Button>
          ) : <Button variant="contained" onClick={() => navigate('/activity')}>Open Activity</Button>}
        </Stack>
      </Paper>

      <CreatePublicationDialog
        open={createPublicationOpen}
        listingID={draft.listingId}
        onClose={() => setCreatePublicationOpen(false)}
        onCreated={async () => {
          patchDraft({ plans: {}, workflows: {} });
          await loadListing();
        }}
      />
      <ManagePublicationDialog open={Boolean(managedPublication)} publication={managedPublication} onClose={() => setManagedPublication(null)} onChanged={async () => { patchDraft({ plans: {}, workflows: {} }); await loadListing(); }} />
    </Stack>
  );
};

export default CanonicalLaunchWizard;
